// Core/Scene/EngineCanvas.jsx

import React, {
  useEffect,
  useRef,
  forwardRef,
} from "react";

// Babylon.js Core
import {
  Engine,        // WebGL Engine
  Scene,         // Babylon Scene
  WebGPUEngine,  // WebGPU Engine
} from "@babylonjs/core";

// GLTF / OBJ 등 로더 등록
import "@babylonjs/loaders";

/**
 * EngineCanvas 컴포넌트
 *
 * 역할:
 *  - HTMLCanvasElement 생성
 *  - Babylon Engine(WebGPU / WebGL) 초기화
 *  - Scene 생성
 *  - onSceneReady 콜백으로 상위 컴포넌트에 전달
 *  - 렌더 루프 및 리소스 해제 관리
 *
 * WebGPU 설계 원칙:
 *  - EngineCanvas가 "엔진 초기화 책임"을 
 *  - onSceneReady는 "씬 구성만 담당"
 */
const EngineCanvas = forwardRef(
  ({ onSceneReady, className }, ref) => {

    /* ================================
     *  React Ref 정의
     * ================================ */

    // 실제 렌더링 대상이 되는 <canvas> DOM 참조
    const canvasRef = useRef(null);

    // Babylon Engine 인스턴스 저장
    const engineRef = useRef(null);

    // Babylon Scene 인스턴스 저장
    const sceneRef = useRef(null);

    /* ================================
     *  Babylon 초기화 useEffect
     * ================================ */

    useEffect(() => {
      let engine = null;
      let scene = null;
      let handleResize = null;

      /**
       * Babylon Engine + Scene 초기화 함수
       *
       * WebGPU는 async init이 필요하므로
       * useEffect 내부에서 async 함수로 감싼다.
       */
      const initBabylon = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        /* --------------------------------
         * 1️⃣ Engine 생성
         * -------------------------------- */

        // WebGPU 지원 여부 확인
        console.log('navigator.gpu : ', navigator.gpu);
        if (navigator.gpu) {
          // WebGPU Engine 생성
          engine = new WebGPUEngine(canvas, {
            antialias: true, // MSAA
            stencil: true,   // 스텐실 버퍼 활성화
          });

          // ⭐ WebGPU는 반드시 init 호출
          if (typeof engine.initAsync === "function") {
            await engine.initAsync();
          } else if (typeof engine.init === "function") {
            await engine.init();
          }
          // await engine.initAsync?.() ?? await engine.init?.();

          console.log("[Babylon] WebGPU Engine 사용");
        } else {
          // WebGPU 미지원 환경 → WebGL fallback
          engine = new Engine(
            canvas,
            true, // antialias
            {
              preserveDrawingBuffer: true, // 스크린샷 / 후처리
              stencil: true,
            },
            true
          );

          console.log("[Babylon] WebGL Engine 사용");
        }

        /* --------------------------------
         * 2️⃣ Scene 생성
         * -------------------------------- */

        // Engine 초기화 완료 후 Scene 생성
        scene = new Scene(engine);

        // ref에 저장 (외부 접근 가능)
        engineRef.current = engine;
        sceneRef.current = scene;

        /* --------------------------------
         * 3️⃣ 부모 컴포넌트로 전달
         * -------------------------------- */

        // 이 시점의 engine / scene은
        // WebGPU / WebGL 상관없이 완전히 준비된 상태
        if (onSceneReady) {
          await onSceneReady({
            engine,
            scene,
            canvas,
          });
        }

        /* --------------------------------
         * 4️⃣ 렌더링 루프 & 리사이즈
         * -------------------------------- */

        // 초기 캔버스 크기 동기화
        engine.resize();

        // 브라우저 리사이즈 대응
        handleResize = () => engine.resize();
        window.addEventListener("resize", handleResize);

        // Babylon 렌더 루프
        engine.runRenderLoop(() => {
          // activeCamera 없으면 render 불가
          if (scene.activeCamera) {
            scene.render();
          }
        });
      };

      // Babylon 초기화 실행
      initBabylon();

      /* ================================
       *  cleanup (언마운트 시)
       * ================================ */
      return () => {
        window.removeEventListener("resize", handleResize);

        // 전체화면 상태에서는 dispose 지연
        if (!document.fullscreenElement) {
          scene?.dispose();
          engine?.dispose();
        }
      };
    }, []);

    /* ================================
     *  forwardRef 처리
     * ================================ */

    // 부모 컴포넌트에서 canvas DOM 접근 가능
    useEffect(() => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(canvasRef.current);
      } else {
        ref.current = canvasRef.current;
      }
    }, [ref]);

    /* ================================
     *  Render
     * ================================ */

    return (
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    );
  }
);

export default EngineCanvas;
